/**
 * 加密器工厂
 *
 * 优先使用 Web Crypto API 原生实现，非安全上下文自动切换为纯 JS 实现。
 */

import {
  decryptAESFallback,
  encryptAESFallback,
  generateSignatureFallback,
  sha256Fallback,
  verifySignatureFallback,
} from './crypto-js-fallback';
import {
  decryptAESNative,
  encryptAESNative,
  generateSignatureNative,
  isWebCryptoAvailable,
  sha256Native,
  verifySignatureNative,
} from './crypto-native';

/** 统一的加密器接口 */
export interface UnifiedCryptoProvider {
  encryptAES(plaintext: string, keyHex: string): Promise<string>;
  decryptAES(encryptedBase64: string, keyHex: string): Promise<string>;
  generateSignature(data: string, keyHex: string): Promise<string>;
  verifySignature(
    data: string,
    signature: string,
    keyHex: string,
  ): Promise<boolean>;
  sha256(data: string): Promise<string>;
}

/** 加密器实现类型 */
export type CryptoProviderType = 'native' | 'js-fallback';

let _provider: UnifiedCryptoProvider | null = null;
let _providerType: CryptoProviderType | null = null;
let _initialized = false;

function createNativeProvider(): UnifiedCryptoProvider {
  return {
    encryptAES: encryptAESNative,
    decryptAES: decryptAESNative,
    generateSignature: generateSignatureNative,
    verifySignature: verifySignatureNative,
    sha256: sha256Native,
  };
}

function createFallbackProvider(): UnifiedCryptoProvider {
  return {
    encryptAES: encryptAESFallback,
    decryptAES: decryptAESFallback,
    generateSignature: generateSignatureFallback,
    verifySignature: verifySignatureFallback,
    sha256: sha256Fallback,
  };
}

function initializeProvider(): void {
  if (_initialized) return;

  if (isWebCryptoAvailable()) {
    _provider = createNativeProvider();
    _providerType = 'native';
  } else {
    console.error(
      '[Crypto-Factory] Web Crypto API 不可用（非安全上下文），已切换为纯 JS 实现，建议使用 HTTPS 访问',
    );
    _provider = createFallbackProvider();
    _providerType = 'js-fallback';
  }
  _initialized = true;
}

function ensureProvider(): UnifiedCryptoProvider {
  if (!_initialized) initializeProvider();
  return _provider!;
}

// ────────── 公共 API ──────────

/** 获取加密器实例（单例模式） */
export function getCryptoProvider(): UnifiedCryptoProvider {
  return ensureProvider();
}

/** 获取当前加密器类型 */
export function getProviderType(): CryptoProviderType {
  if (!_initialized) initializeProvider();
  return _providerType!;
}

/** 重置加密器（用于测试） */
export function resetProvider(): void {
  _provider = null;
  _providerType = null;
  _initialized = false;
}

/** 是否使用原生加密器 */
export function isUsingNativeProvider(): boolean {
  return getProviderType() === 'native';
}

// ────────── 便捷导出 ──────────

export async function encryptAESOptimized(
  plaintext: string,
  keyHex: string,
): Promise<string> {
  return ensureProvider().encryptAES(plaintext, keyHex);
}

export async function decryptAESOptimized(
  encryptedBase64: string,
  keyHex: string,
): Promise<string> {
  return ensureProvider().decryptAES(encryptedBase64, keyHex);
}

export async function generateSignatureOptimized(
  data: string,
  keyHex: string,
): Promise<string> {
  return ensureProvider().generateSignature(data, keyHex);
}

export async function verifySignatureOptimized(
  data: string,
  signature: string,
  keyHex: string,
): Promise<boolean> {
  return ensureProvider().verifySignature(data, signature, keyHex);
}

export async function sha256Optimized(data: string): Promise<string> {
  return ensureProvider().sha256(data);
}
